// client/src/pages/Cart.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Trash2, ShoppingBag, ArrowLeft, CheckCircle, XCircle, Globe } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import ConfirmationModal from '@/components/ConfirmationModal';
import storeService, { CartItem, CartSummary } from '@/api/services/storeService';

const Cart = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [cartItems, setCartItems] = useState<CartItem[] | null>(null);
  const [summary, setSummary] = useState<CartSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [removingItems, setRemovingItems] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [confirmModal, setConfirmModal] = useState<{ isOpen: boolean; itemId: string | null }>({
    isOpen: false,
    itemId: null,
  });

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const fetchCart = useCallback(async () => {
    try {
      setLoading(true);
      const { items, summary: cartSummary } = await storeService.getCart();
      setCartItems(items);
      setSummary(cartSummary);
    } catch (error: unknown) {
      showMessage('error', t('operator.commerce.cart.fetchError', { defaultValue: 'Failed to load cart. Please try again.' }));
      setCartItems([]);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-next-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      // setLanguage(lng?.toLowerCase().startsWith("fr") ? "fr" : "en");
    };

    i18n.on("languageChanged", handleLanguageChange);
    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, [i18n]);

  const handleRemoveItem = (itemId: string) => {
    setConfirmModal({ isOpen: true, itemId });
  };

  const confirmRemoveItem = async () => {
    const itemId = confirmModal.itemId;
    if (!itemId) return;

    setConfirmModal({ isOpen: false, itemId: null });
    setRemovingItems(prev => new Set(prev).add(itemId));

    try {
      await storeService.removeFromCart(itemId);
      // Re-fetch to get updated server-computed summary
      const { items, summary: cartSummary } = await storeService.getCart();
      setCartItems(items);
      setSummary(cartSummary);
      showMessage('success', t('operator.commerce.cart.removeSuccess', { defaultValue: 'Item removed from cart successfully' }));
      window.dispatchEvent(new Event('cartUpdated'));
    } catch (error: unknown) {
      showMessage('error', t('operator.commerce.cart.removeError', { defaultValue: 'Failed to remove item. Please try again.' }));
    } finally {
      setRemovingItems(prev => {
        const s = new Set(prev);
        s.delete(itemId);
        return s;
      });
    }
  };

  const handleCheckout = () => {
    if (!cartItems || cartItems.length === 0) {
      alert(t('operator.commerce.cart.emptyAlert', { defaultValue: 'Your cart is empty' }));
      return;
    }
    navigate('/operator/checkout');
  };

  if (loading || cartItems === null) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t('operator.commerce.cart.meta.title', { defaultValue: 'Shopping Cart | KonectX' })}</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-black">
        <div className="max-w-7xl mx-auto px-4 py-8">
          {message && (
            <div
              className={`mb-6 p-4 rounded-lg flex items-center gap-2 ${
                message.type === 'success'
                  ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400'
                  : 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
              }`}
            >
              {message.type === 'success' ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                <XCircle className="w-5 h-5" />
              )}
              <span className="text-sm">{message.text}</span>
            </div>
          )}

          <div className="mb-8">
            <button
              onClick={() => navigate('/operator/store')}
              className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-blue-600 mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('operator.commerce.common.continueShopping', { defaultValue: 'Continue Shopping' })}
            </button>
            <div className="flex items-center justify-between">
              <h1 className="text-3xl font-bold text-gray-800 dark:text-white">
                {t('operator.commerce.cart.title', { defaultValue: 'Shopping Cart' })}
              </h1>
              <button
                type="button"
                onClick={() => {
                  const next = i18n.language === 'fr' ? 'en' : 'fr';
                  i18n.changeLanguage(next);
                  localStorage.setItem('i18nextLng', next);
                }}
                className="p-2 rounded-lg bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-700 transition-colors"
                title="Toggle Language"
              >
                <Globe className="w-5 h-5" />
              </button>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              {t('operator.commerce.cart.inCart', {
                defaultValue: '{{count}} items in your cart',
                count: summary?.itemCount ?? cartItems.length,
              })}
            </p>
          </div>

          {cartItems.length === 0 ? (
            <div className="bg-white dark:bg-[#333436] rounded-lg p-12 text-center">
              <ShoppingBag className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-gray-800 dark:text-white mb-2">
                {t('operator.commerce.cart.emptyTitle', { defaultValue: 'Your cart is empty' })}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {t('operator.commerce.cart.emptyBody', {
                  defaultValue: "Looks like you haven't added anything to your cart yet",
                })}
              </p>
              <button
                onClick={() => navigate('/operator/store')}
                className="px-8 py-3 bg-gray-800 dark:bg-blue-700 text-white rounded-lg hover:bg-gray-900 dark:hover:bg-blue-600 transition-colors"
              >
                {t('operator.commerce.common.startShopping', { defaultValue: 'Start Shopping' })}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Items */}
              <div className="lg:col-span-2 space-y-4">
                {cartItems.map((item) => {
                  const isRemoving = removingItems.has(item.id);

                  return (
                    <div
                      key={item.id}
                      className="bg-white dark:bg-[#333436] rounded-lg p-6 flex gap-6 items-center relative"
                    >
                      {isRemoving && (
                        <div className="absolute inset-0 bg-white/80 dark:bg-black/80 rounded-lg flex items-center justify-center z-10">
                          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                        </div>
                      )}

                      <div className="w-24 h-24 bg-gray-100 dark:bg-[#1a1b1e] rounded-lg flex items-center justify-center flex-shrink-0">
                        <img
                          src={item.thumbnailUrl ?? '/store/store1.png'}
                          alt={item.name}
                          className="w-full h-full object-contain p-2"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src =
                              'https://placehold.co/96x96/1a1a2e/60a5fa?text=KX';
                          }}
                        />
                      </div>

                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-1">
                          {item.name}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                          {t('operator.commerce.common.quantity', { defaultValue: 'Quantity' })}: {item.quantity}
                        </p>
                        {/* Use server-computed lineTotal � no client-side multiplication needed */}
                        <p className="text-xl font-bold text-gray-800 dark:text-white">
                          ${item.lineTotal.toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          ${item.unitPrice.toFixed(2)}{' '}
                          {t('operator.commerce.common.each', { defaultValue: 'each' })}
                        </p>
                      </div>

                      <button
                        onClick={() => handleRemoveItem(item.id)}
                        disabled={isRemoving}
                        className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                        aria-label={t('operator.commerce.common.remove', { defaultValue: 'Remove' })}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Order Summary � uses server-computed values from CartSummary */}
              <div className="lg:col-span-1">
                <div className="bg-white dark:bg-[#333436] rounded-lg p-6 sticky top-4">
                  <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-6">
                    {t('operator.commerce.common.orderSummary', { defaultValue: 'Order Summary' })}
                  </h2>
                  <div className="space-y-4 mb-6">
                    <div className="flex justify-between text-gray-600 dark:text-gray-400">
                      <span>{t('operator.commerce.common.subtotal', { defaultValue: 'Subtotal' })}</span>
                      <span className="font-semibold">
                        ${(summary?.subtotal ?? 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-gray-600 dark:text-gray-400">
                      <span>{t('operator.commerce.common.platformFee', { defaultValue: 'Platform Fee' })}</span>
                      <span className="font-semibold">
                        ${(summary?.platformFee ?? 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                      <div className="flex justify-between text-lg font-bold text-gray-800 dark:text-white">
                        <span>{t('operator.commerce.common.total', { defaultValue: 'Total' })}</span>
                        <span>${(summary?.total ?? 0).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={handleCheckout}
                    className="w-full py-3 bg-gray-800 dark:bg-blue-700 text-white rounded-lg hover:bg-gray-900 dark:hover:bg-blue-600 transition-colors font-semibold"
                  >
                    {t('operator.commerce.cart.proceedToCheckout', { defaultValue: 'Proceed to Checkout' })}
                  </button>
                  <button
                    onClick={() => navigate('/operator/store')}
                    className="w-full mt-3 py-3 bg-white dark:bg-[#1a1b1e] border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-[#252628] transition-colors"
                  >
                    {t('operator.commerce.common.continueShopping', { defaultValue: 'Continue Shopping' })}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, itemId: null })}
        onConfirm={confirmRemoveItem}
        title={t('operator.commerce.cart.confirmRemoveTitle', { defaultValue: 'Remove Item' })}
        message={t('operator.commerce.cart.confirmRemoveBody', {
          defaultValue: 'Are you sure you want to remove this item from your cart?',
        })}
        confirmText={t('operator.commerce.common.remove', { defaultValue: 'Remove' })}
        cancelText={t('operator.commerce.common.cancel', { defaultValue: 'Cancel' })}
        variant="danger"
      />
    </>
  );
};

export default Cart;
